import { Directive, inject } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { LoggerService } from '../../services/logger.service';
import { WidgetUtilService } from '../../services/widget-util.service';

/**
 * Directive to protect input and textarea fields from phishing links.
 * It reads dynamic settings from widgetUtilService:
 *  - enable_phishing_protection (boolean): to enable/disable validation
 *  - phishing_protection_regex (string): regex pattern to validate against
 */
/**
 * Builds the configured safe-text regex, or `null` when protection is off or the pattern is
 * unusable. Exported so forms that bind without a control (the invoice address form) apply the
 * same rule as this directive instead of carrying a second copy of it.
 */
export function buildPhishingSafeTextRegex(pattern: string | undefined | null): RegExp | null {
  if (!pattern) {
    return null;
  }
  try {
    // Remove starting and ending slashes, and extract pattern and flags
    const match = pattern.match(/^\/(.*)\/([a-z]*)$/i);
    // `g`/`y` make `.test()` stateful via lastIndex, so one instance reused across several
    // fields would test the second field from where the first stopped. Nothing here relies on
    // them — the directive tests a single value per call.
    const flags = match ? match[2].replace(/[gy]/g, '') : 'i';

    return match ? new RegExp(match[1], flags) : new RegExp(pattern, flags);
  } catch (error) {
    LoggerService.error('Invalid phishing protection regex configured:', pattern, error);

    return null;
  }
}

@Directive({
  selector: '[phishingProtection]',
  standalone: true,
  providers: [{ provide: NG_VALIDATORS, useExisting: PhishingProtectionDirective, multi: true }]
})
export class PhishingProtectionDirective implements Validator {

  private widgetUtilService = inject(WidgetUtilService);

  validate(control: AbstractControl): ValidationErrors | null {

    const isProtectionEnabled = this.widgetUtilService.widgetConf?.enable_phishing_protection;
    const phishingRegexPattern = this.widgetUtilService.widgetConf?.phishing_protection_regex;

    // If protection feature is disabled, skip validation completely
    if (!isProtectionEnabled) {
      return null;
    }
    const safeTextRegex = buildPhishingSafeTextRegex(phishingRegexPattern);
    if (!safeTextRegex) {
      return null;
    }

    // If input value exists and does NOT match safe text regex ➔ Mark as invalid
    if (control.value && !safeTextRegex.test(control.value)) {
      return { phishingLinkDetected: true }; // Custom validation error
    }
    // Otherwise, input is valid
    return null;
  }
}
